(() => {
  if (customElements.get('gift-offer-widget')) return;

  const MODES = Object.freeze({
    SUBSCRIPTION: 'subscription',
    ONE_TIME: 'one-time',
  });

  const PHASES = Object.freeze({
    IDLE: 'idle',
    ADDING: 'adding',
  });

  class ShopifyCartAdapter {
    get component() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    async add(items) {
      const cart = this.component;
      const payload = { items };
      const configuredCartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
      const cartAddUrl = configuredCartAddUrl.endsWith('.js')
        ? configuredCartAddUrl
        : `${configuredCartAddUrl.replace(/\/$/, '')}.js`;

      if (cart?.getSectionsToRender) {
        payload.sections = [...new Set(
          cart.getSectionsToRender().map((section) => section.section || section.id).filter(Boolean)
        )];
        payload.sections_url = window.location.pathname;
        cart.setActiveElement?.(document.activeElement);
      }

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });
      const responseText = await response.text();
      let result;

      try {
        result = JSON.parse(responseText);
      } catch (error) {
        const message = response.status === 429
          ? 'Shopify is receiving too many requests. Please wait a moment and try again.'
          : 'Shopify returned an unexpected cart response. Please refresh and try again.';
        throw new Error(message);
      }

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Could not add this offer to the cart.');
      }

      return result;
    }

    async present(result, variantId) {
      const cart = this.component;

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'gift-offer-widget',
          productVariantId: variantId,
          cartData: result,
        });
      }

      if (cart?.renderContents && result.sections) {
        cart.classList.remove('is-empty');
        cart.renderContents(result);
        return;
      }

      window.location.assign(window.routes?.cart_url || '/cart');
    }
  }

  class GiftOfferWidget extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.config = this.readConfig();
      this.tiers = Array.isArray(this.config.tiers) ? this.config.tiers.filter((tier) => tier.variantId) : [];
      this.gifts = Array.isArray(this.config.gifts) ? this.config.gifts.filter((gift) => gift.productId) : [];
      this.revealedGiftKeys = new Set();
      this.cartAdapter = new ShopifyCartAdapter();
      this.detailsPanel = this.querySelector('[data-offer-details]');

      const defaultTier = this.tiers.find((tier) => String(tier.id) === String(this.config.defaultTierId))
        || this.tiers.find((tier) => Number(tier.level) === Number(this.config.defaultTierLevel));
      const firstAvailableTier = this.tiers.find((tier) => tier.available) || this.tiers[0];
      const selectedTier = defaultTier?.available ? defaultTier : firstAvailableTier;

      this.state = {
        mode: this.config.defaultMode === MODES.ONE_TIME ? MODES.ONE_TIME : MODES.SUBSCRIPTION,
        tierId: selectedTier?.id ?? null,
        giftSelections: this.defaultGiftSelections(),
        phase: PHASES.IDLE,
        error: this.config.error || '',
      };

      this.onClick = this.handleClick.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);

      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
    }

    readConfig() {
      const configElement = this.querySelector('[data-offer-config]');

      if (!configElement) return {};

      try {
        return JSON.parse(configElement.textContent);
      } catch (error) {
        return { error: 'This offer is temporarily unavailable.' };
      }
    }

    defaultGiftSelections() {
      return this.gifts.reduce((selections, gift) => {
        if (!gift.requiresSelection && gift.defaultVariantId) {
          selections[gift.key] = gift.defaultVariantId;
        }
        return selections;
      }, {});
    }

    get selectedTier() {
      return this.tiers.find((tier) => String(tier.id) === String(this.state.tierId)) || null;
    }

    deriveState() {
      const tier = this.selectedTier;
      const isSubscription = this.state.mode === MODES.SUBSCRIPTION;
      const unlockedGifts = isSubscription && tier
        ? this.gifts
          .map((gift) => {
            const quantity = Number(gift.quantities?.[tier.level] || 0);
            const unitValue = Number(gift.unitValue ?? gift.value ?? 0);
            return { ...gift, quantity, value: unitValue * quantity };
          })
          .filter((gift) => gift.available && gift.quantity > 0)
        : [];
      const missingGiftSelection = unlockedGifts.find((gift) => {
        if (!gift.requiresSelection) return false;
        const selectedVariantId = this.state.giftSelections[gift.key];
        return !gift.variants.some((variant) => variant.available && String(variant.id) === String(selectedVariantId));
      });
      const missingSellingPlan = Boolean(isSubscription && tier && !tier.sellingPlanId);
      const canSubmit = Boolean(
        tier
        && tier.available
        && this.state.phase !== PHASES.ADDING
        && !missingGiftSelection
        && !missingSellingPlan
      );

      return {
        tier,
        isSubscription,
        unlockedGifts,
        missingGiftSelection,
        missingSellingPlan,
        canSubmit,
      };
    }

    handleClick(event) {
      const modeToggle = event.target.closest('[data-mode-toggle]');
      if (modeToggle && this.contains(modeToggle)) {
        if (this.state.phase === PHASES.ADDING) return;
        if (this.state.mode === MODES.SUBSCRIPTION && this.config.oneTimeDirectAdd) {
          this.submitOneTimeDirect();
          return;
        }
        const nextMode = this.state.mode === MODES.SUBSCRIPTION ? MODES.ONE_TIME : MODES.SUBSCRIPTION;
        this.setState({ mode: nextMode, error: '' });
        return;
      }

      const modeButton = event.target.closest('[data-mode-choice]');
      if (modeButton && this.contains(modeButton)) {
        this.setState({ mode: modeButton.dataset.modeChoice, error: '' });
        return;
      }

      const backToSubscription = event.target.closest('[data-back-to-subscription]');
      if (backToSubscription && this.contains(backToSubscription)) {
        this.setState({ mode: MODES.SUBSCRIPTION, error: '' });
        return;
      }

      const giftCover = event.target.closest('[data-reveal-gift]');
      if (giftCover && this.contains(giftCover)) {
        this.revealedGiftKeys.add(giftCover.dataset.revealGift);
        this.render();
        return;
      }

      const giftVariant = event.target.closest('[data-gift-variant]');
      if (giftVariant && this.contains(giftVariant)) {
        const giftKey = giftVariant.dataset.giftKey;
        this.setState({
          giftSelections: {
            ...this.state.giftSelections,
            [giftKey]: Number(giftVariant.dataset.giftVariant),
          },
          error: '',
        });
        return;
      }

      const submitButton = event.target.closest('[data-offer-submit]');
      if (submitButton && this.contains(submitButton)) {
        this.submit();
      }
    }

    handleChange(event) {
      const tierInput = event.target.closest('[data-tier-choice]');
      if (!tierInput || !this.contains(tierInput)) return;

      this.setState({ tierId: tierInput.value, error: '' });
    }

    setState(patch) {
      this.state = { ...this.state, ...patch };
      this.render();
    }

    render() {
      if (!this.state) return;

      const derived = this.deriveState();
      this.classList.toggle('is-subscription', derived.isSubscription);
      this.classList.toggle('is-one-time', !derived.isSubscription);
      this.setAttribute('aria-busy', String(this.state.phase === PHASES.ADDING));

      this.querySelectorAll('[data-mode-choice]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.modeChoice === this.state.mode));
      });

      this.querySelectorAll('[data-tier]').forEach((tierElement) => {
        const tier = this.tiers.find((candidate) => String(candidate.id) === tierElement.dataset.tier);
        const selected = tier && String(tier.id) === String(this.state.tierId);
        const input = tierElement.querySelector('[data-tier-choice]');
        const finalPrice = tierElement.querySelector('[data-final-price]');
        const comparePrice = tierElement.querySelector('[data-compare-price]');
        const perDayPrice = tierElement.querySelector('[data-per-day-price]');
        const giftCountElement = tierElement.querySelector('[data-tier-gift-count]');

        tierElement.classList.toggle('is-selected', Boolean(selected));
        tierElement.classList.toggle('is-unavailable', !tier?.available);

        if (input) {
          input.checked = Boolean(selected);
          input.disabled = !tier?.available;
        }

        if (finalPrice && tier) {
          finalPrice.textContent = this.formatMoney(
            derived.isSubscription ? tier.subscriptionPrice : tier.oneTimePrice
          );
        }

        if (comparePrice) comparePrice.hidden = !derived.isSubscription;

        if (perDayPrice && tier) {
          const activePrice = derived.isSubscription ? tier.subscriptionPrice : tier.oneTimePrice;
          const daysSupply = Math.max(1, Number(tier.daysSupply || 1));
          perDayPrice.textContent = `${this.formatMoney(activePrice / daysSupply)} / day`;
        }

        if (giftCountElement && tier) {
          const giftCount = this.gifts.reduce(
            (total, gift) => total + (gift.available ? Number(gift.quantities?.[tier.level] || 0) : 0),
            0
          );
          if (this.config.oneTimeDirectAdd) {
            giftCountElement.hidden = giftCount === 0;
            giftCountElement.textContent = `${giftCount} FREE GIFT${giftCount === 1 ? '' : 'S'}`;
          } else {
            const subscriptionSavings = Math.max(
              0,
              Number(tier.oneTimePrice || 0) - Number(tier.subscriptionPrice || 0)
            );
            const savingsPrefix = derived.isSubscription ? 'SUBSCRIBE' : 'SWITCH TO SUBSCRIBE';
            const savingsText = subscriptionSavings > 0
              ? `${savingsPrefix} & SAVE ${this.formatMoney(subscriptionSavings)}`
              : `${savingsPrefix} & SAVE MORE`;
            const giftsText = giftCount > 0
              ? ` + GET ${giftCount} FREE GIFT${giftCount === 1 ? '' : 'S'}`
              : '';

            giftCountElement.hidden = subscriptionSavings === 0 && giftCount === 0;
            giftCountElement.textContent = `🎁 ${savingsText}${giftsText}`;
          }
        }

      });

      const selectedTierElement = this.querySelector(`[data-tier="${this.state.tierId}"]`);
      const selectedTierSlot = selectedTierElement?.querySelector('[data-tier-slot]');
      if (this.detailsPanel && selectedTierSlot && this.detailsPanel.parentElement !== selectedTierSlot) {
        selectedTierSlot.append(this.detailsPanel);
      }
      if (this.detailsPanel) this.detailsPanel.hidden = !derived.isSubscription;

      const billingFrequency = this.querySelector('[data-billing-frequency]');
      if (billingFrequency && derived.tier) {
        const deliveryWeeks = Number(derived.tier.deliveryWeeks || 0);
        billingFrequency.hidden = deliveryWeeks <= 0;
        billingFrequency.textContent = deliveryWeeks > 0
          ? `Shipped and billed every ${deliveryWeeks} week${deliveryWeeks === 1 ? '' : 's'}`
          : '';
      }

      const unlockedGiftKeys = new Set(derived.unlockedGifts.map((gift) => gift.key));
      this.querySelectorAll('[data-gift]').forEach((giftElement) => {
        const giftKey = giftElement.dataset.gift;
        const unlocked = unlockedGiftKeys.has(giftKey);
        const gift = derived.unlockedGifts.find((candidate) => candidate.key === giftKey);
        const quantity = giftElement.querySelector('[data-gift-quantity]');
        giftElement.classList.toggle('is-unlocked', unlocked);
        giftElement.classList.toggle('is-locked', !unlocked);
        giftElement.classList.toggle('is-not-included', !unlocked);
        giftElement.classList.toggle('is-revealed', this.revealedGiftKeys.has(giftKey));
        if (quantity) quantity.textContent = gift ? `×${gift.quantity} FREE` : '';
      });

      this.querySelectorAll('[data-gift-selector]').forEach((selector) => {
        const giftKey = selector.dataset.giftSelector;
        selector.classList.toggle('is-visible', unlockedGiftKeys.has(giftKey));
      });

      this.querySelectorAll('[data-gift-variant]').forEach((button) => {
        const selectedVariantId = this.state.giftSelections[button.dataset.giftKey];
        button.setAttribute('aria-pressed', String(String(selectedVariantId) === button.dataset.giftVariant));
      });

      const giftsValue = derived.unlockedGifts.reduce((total, gift) => total + Number(gift.value || 0), 0);
      const giftsValueElement = this.querySelector('[data-gifts-value]');
      if (giftsValueElement) {
        const giftCount = derived.unlockedGifts.reduce((total, gift) => total + gift.quantity, 0);
        giftsValueElement.textContent = this.config.oneTimeDirectAdd
          ? `${giftCount} FREE GIFT${giftCount === 1 ? '' : 'S'} (${this.formatMoney(giftsValue)} VALUE)`
          : `${this.formatMoney(giftsValue)} value — FREE`;
      }

      const giftSummary = this.querySelector('[data-tier-gift-summary]');
      if (giftSummary) {
        const giftCount = derived.unlockedGifts.reduce((total, gift) => total + gift.quantity, 0);
        giftSummary.textContent = `${giftCount} free gift${giftCount === 1 ? '' : 's'} — ${this.formatMoney(giftsValue)} value, yours FREE`;
      }

      const oneTimePrice = this.querySelector('[data-one-time-price]');
      if (oneTimePrice && derived.tier) oneTimePrice.textContent = this.formatMoney(derived.tier.oneTimePrice);

      const modeToggle = this.querySelector('[data-mode-toggle]');
      const modeToggleLabel = this.querySelector('[data-mode-toggle-label]');
      const modeTogglePrice = this.querySelector('[data-mode-toggle-price]');
      if (modeToggle && derived.tier) {
        const nextMode = derived.isSubscription ? MODES.ONE_TIME : MODES.SUBSCRIPTION;
        const nextPrice = derived.isSubscription ? derived.tier.oneTimePrice : derived.tier.subscriptionPrice;
        modeToggle.dataset.nextMode = nextMode;
        modeToggle.disabled = this.state.phase === PHASES.ADDING;
        modeToggle.setAttribute(
          'aria-label',
          derived.isSubscription && this.config.oneTimeDirectAdd
            ? 'Add selected one-time purchase to cart'
            : (derived.isSubscription ? 'Switch to one-time purchase' : 'Switch to subscription')
        );
        if (modeToggleLabel) {
          modeToggleLabel.textContent = derived.isSubscription
            ? (this.config.labels?.oneTimePurchase || 'One-time purchase')
            : (this.config.labels?.switchToSubscription || 'Switch to subscribe');
        }
        if (modeTogglePrice) modeTogglePrice.textContent = this.formatMoney(nextPrice);
      }

      const submitButton = this.querySelector('[data-offer-submit]');
      if (submitButton) {
        submitButton.disabled = !derived.canSubmit;
        submitButton.textContent = this.submitLabel(derived);
      }

      const status = this.querySelector('[data-offer-status]');
      if (status) {
        status.hidden = !this.state.error;
        status.textContent = this.state.error;
      }
    }

    submitLabel(derived) {
      if (this.state.phase === PHASES.ADDING) return this.config.labels?.adding || 'ADDING…';
      if (derived.missingSellingPlan) return this.config.labels?.unavailable || 'SUBSCRIPTION UNAVAILABLE';
      if (derived.missingGiftSelection) return this.config.labels?.chooseVariant || 'CHOOSE YOUR SIZE ↑';
      if (!derived.tier?.available) return this.config.labels?.soldOut || 'SOLD OUT';
      return this.config.labels?.addToCart || 'ADD TO CART';
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;

      try {
        return new Intl.NumberFormat(this.config.locale || document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.config.currency || 'USD',
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    buildItems(derived) {
      const bundleKey = `gift-offer-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const mainItem = {
        id: Number(derived.tier.variantId),
        quantity: Number(derived.tier.quantity || 1),
        properties: {
          _gift_offer_bundle: bundleKey,
          _gift_offer_tier: String(derived.tier.id),
        },
      };

      if (derived.isSubscription) mainItem.selling_plan = Number(derived.tier.sellingPlanId);

      const giftItems = derived.unlockedGifts.map((gift) => ({
        id: Number(this.state.giftSelections[gift.key] || gift.defaultVariantId),
        quantity: gift.quantity,
        properties: {
          _free_gift: 'true',
          _gift_offer_bundle: bundleKey,
          _gift_offer_tier: String(derived.tier.id),
          Gift: this.config.labels?.giftProperty || 'Free gift — Subscribe & Save',
        },
      }));

      return [mainItem, ...giftItems];
    }

    async submitOneTimeDirect() {
      const tier = this.selectedTier;
      if (!tier?.available || this.state.phase === PHASES.ADDING) return;

      const oneTimeSelection = {
        tier,
        isSubscription: false,
        unlockedGifts: [],
      };

      this.setState({ phase: PHASES.ADDING, error: '' });

      try {
        const result = await this.cartAdapter.add(this.buildItems(oneTimeSelection));

        this.dispatchEvent(new CustomEvent('gift-offer:added', {
          bubbles: true,
          detail: { cartData: result, tier, mode: MODES.ONE_TIME },
        }));

        await this.cartAdapter.present(result, tier.variantId);
        this.setState({ phase: PHASES.IDLE, error: '' });
      } catch (error) {
        this.setState({
          phase: PHASES.IDLE,
          error: error instanceof Error ? error.message : 'Could not add this offer to the cart.',
        });
      }
    }

    async submit() {
      const derived = this.deriveState();
      if (!derived.canSubmit) return;

      this.setState({ phase: PHASES.ADDING, error: '' });

      try {
        const result = await this.cartAdapter.add(this.buildItems(derived));

        this.dispatchEvent(new CustomEvent('gift-offer:added', {
          bubbles: true,
          detail: { cartData: result, tier: derived.tier, mode: this.state.mode },
        }));

        if (this.config.redirectToCheckout) {
          window.location.assign('/checkout');
          return;
        }

        await this.cartAdapter.present(result, derived.tier.variantId);
      } catch (error) {
        this.setState({
          phase: PHASES.IDLE,
          error: error instanceof Error ? error.message : 'Could not add this offer to the cart.',
        });
        return;
      }

      this.setState({ phase: PHASES.IDLE, error: '' });
    }

    reportError(message) {
      const status = this.querySelector('[data-offer-status]');
      if (!status) return;
      status.hidden = false;
      status.textContent = message;
    }
  }

  customElements.define('gift-offer-widget', GiftOfferWidget);
})();
